import { Search } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useIsMobile } from "@/hooks/use-mobile";

import { StockOpnameItemCard } from "./stock-opname-item-card";
import { StockOpnameItemRow } from "./stock-opname-item-row";
import { type OpnameItemErrors } from "./stock-opname-schema";

import type {
  StockOpnameItem,
  StockOpnameType,
  StockReason,
} from "@artisancode/api-types";

interface Props {
  opnameType: StockOpnameType;
  items: StockOpnameItem[];
  reasons: StockReason[];
  query: string;
  onQueryChange: (query: string) => void;
  onlyDiscrepancies: boolean;
  onToggleOnlyDiscrepancies: () => void;
  discrepancyCount: number;
  onOnHandChange: (itemId: string, value: string) => void;
  onRepackingChange?: (itemId: string, value: string) => void;
  onReasonChange: (itemId: string, reasonId: string) => void;
  onNoteChange: (itemId: string, note: string) => void;
  isLocked: boolean;
  onRemove?: (lineId: string) => void;
  errors?: OpnameItemErrors;
  /** Shown when there are no lines to list. */
  emptyText: string;
}

export function StockOpnameItemsTable({
  opnameType,
  items,
  reasons,
  query,
  onQueryChange,
  onlyDiscrepancies,
  onToggleOnlyDiscrepancies,
  discrepancyCount,
  onOnHandChange,
  onRepackingChange,
  onReasonChange,
  onNoteChange,
  isLocked,
  onRemove,
  errors = {},
  emptyText,
}: Props) {
  const isMobile = useIsMobile();
  const isRawMaterial = opnameType === "raw_material";
  const itemProps = {
    reasons,
    isRawMaterial,
    isLocked,
    onOnHandChange,
    onRepackingChange,
    onReasonChange,
    onNoteChange,
    onRemove,
  };
  const canRemove = !isLocked && Boolean(onRemove);

  return (
    <>
      {/* Toolbar filter */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 sm:px-6 py-2.5 border-b bg-muted/20">
        <div className="relative w-full sm:flex-1 sm:max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Cari nama barang atau SKU..."
            className="h-9 pl-8 text-xs bg-background"
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Button
            type="button"
            variant={onlyDiscrepancies ? "secondary" : "outline"}
            size="sm"
            onClick={onToggleOnlyDiscrepancies}
            className="min-h-[40px] sm:min-h-0 sm:h-8 text-xs w-full sm:w-auto"
          >
            {onlyDiscrepancies ? "Tampilkan Semua" : "Filter Hanya Selisih"}
            {discrepancyCount > 0 && (
              <Badge
                variant="destructive"
                className="ml-1.5 h-4 px-1 text-[10px]"
              >
                {discrepancyCount}
              </Badge>
            )}
          </Button>
        </div>
      </div>

      <div className="px-3 py-2 sm:px-6">
        {isMobile ? (
          items.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              {emptyText}
            </div>
          ) : (
            <div className="flex flex-col gap-3 py-1">
              {items.map((item) => (
                <StockOpnameItemCard
                  key={item.id}
                  item={item}
                  error={errors[item.id]}
                  {...itemProps}
                />
              ))}
            </div>
          )
        ) : (
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-background shadow-sm">
              <TableRow className="text-xs">
                <TableHead className="w-[220px]">Nama Barang</TableHead>
                <TableHead className="w-[70px]">Satuan</TableHead>
                <TableHead className="w-[100px] text-right">
                  {isRawMaterial ? "Sistem (Utuh)" : "Stok Sistem"}
                </TableHead>
                <TableHead className="w-[110px] text-right">
                  {isRawMaterial ? "Fisik (Utuh)" : "Stok Fisik"}
                </TableHead>
                <TableHead className="w-[90px] text-center">Selisih</TableHead>
                {isRawMaterial && (
                  <>
                    <TableHead className="w-[100px] border-l pl-3 text-right">
                      Sistem (Bar)
                    </TableHead>
                    <TableHead className="w-[140px] text-right">
                      Fisik (Bar)
                    </TableHead>
                    <TableHead className="w-[90px] text-center">
                      Selisih Bar
                    </TableHead>
                  </>
                )}
                <TableHead className="min-w-[200px]">
                  Alasan & Catatan
                </TableHead>
                {canRemove && <TableHead className="w-10" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={(isRawMaterial ? 9 : 6) + (canRemove ? 1 : 0)}
                    className="py-10 text-center text-sm text-muted-foreground"
                  >
                    {emptyText}
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <StockOpnameItemRow
                    key={item.id}
                    item={item}
                    error={errors[item.id]}
                    {...itemProps}
                  />
                ))
              )}
            </TableBody>
          </Table>
        )}
      </div>
    </>
  );
}
